import { type FC } from 'react';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';

import { PRICE_STATS } from '../sbSharedRefineMenuHelpers.ts';
import { CURRENCY } from '../sbSharedRefineMenuConstants.js';
import { areFiltersApplied } from '../../helpers/appliedFilters/areFiltersApplied.ts';
import { type RefineMenuFilter } from '../../SbSharedRefineMenu/SbSharedRefineMenuFilters/SbSharedRefineMenuFilters.tsx';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';

// components
import { SbSharedRefineMenuInputRange } from './SbSharedRefineMenuInputRange.jsx';
import { type SbSharedRefineMenuInputRangeCurrency_itemSearch$data } from './__generated__/SbSharedRefineMenuInputRangeCurrency_itemSearch.graphql.ts';

const { labelTo, labelFrom } = defineMessages({
    labelTo: {
        id: 'sb.SbSharedRefineMenuFilterPrice.maximum',
        defaultMessage: '{currency} max',
    },
    labelFrom: {
        id: 'sb.SbSharedRefineMenuFilterPrice.minimum',
        defaultMessage: '{currency} min',
    },
});

type Props = {
    filterName: string;
    filter: RefineMenuFilter;
    applyOnBlur?: boolean;
    currency: string;
    itemSearch: SbSharedRefineMenuInputRangeCurrency_itemSearch$data;
    showInvalidRangeErrorMessage?: boolean;
    size?: 'small' | 'medium' | 'large';
};

const SbSharedRefineMenuInputRangeCurrencyComponent: FC<Props> = props => {
    const { currency, itemSearch } = props;
    const intl = useIntl();
    // The price values should be prefilled if they are coming from appliedFilters
    const showAnimatedPlaceholder = !areFiltersApplied(itemSearch, PRICE_STATS);
    const { isQuickFiltersRedesignVariant } = useServerVarsContext();

    return (
        <SbSharedRefineMenuInputRange
            {...props}
            labelTo={intl.formatMessage(labelTo, { currency })}
            statKey={currency}
            additionalFilter={CURRENCY}
            labelFrom={intl.formatMessage(labelFrom, { currency })}
            showAnimatedPlaceholder={showAnimatedPlaceholder}
            trackCustomRange
            isQuickFiltersRedesignVariant={isQuickFiltersRedesignVariant}
        />
    );
};

export const SbSharedRefineMenuInputRangeCurrency = createFragmentContainer(
    SbSharedRefineMenuInputRangeCurrencyComponent,
    {
        itemSearch: graphql`
            fragment SbSharedRefineMenuInputRangeCurrency_itemSearch on ItemSearchQueryConnection {
                ...SbSharedRefineMenuInputRange_itemSearch
                ...areFiltersApplied_itemSearch
            }
        `,
    }
);
